/**
 * Cockpit Phase 6 — the CockpitSpec schema (COCKPIT-PLAN §4).
 *
 * A CockpitSpec is a TREE of catalog-component nodes — NEVER free-form HTML.
 * Each node names a `component` from the closed `cockpit-library`, carries a
 * `props` bag validated against that component's `propsSchema`, an optional
 * `children` array, and references declared data bindings + actions. The
 * authoring pipeline (Stage A) emits a CockpitSpec; the iframe runtime renderer
 * interprets a CockpitSpec → DOM from the component library; and the publish
 * gate G1 lints a CockpitSpec deterministically against this same schema.
 *
 * This is the "parse (hard)" layer of the 4-layer "only catalog components"
 * enforcement (COCKPIT-PLAN §4): a node referencing a non-catalog component is
 * rejected here, and a node whose props violate the component's propsSchema is
 * rejected here. The remaining layers are prompt (soft), compile (the fixed
 * renderer), and publish (the G1 critical gate).
 *
 * Browser-safe: pure Zod, zero Node imports — consumed by api (gates), web
 * (renderer), and ai-core (authoring validation).
 */

import { z } from 'zod';
import {
  getCockpitComponent,
  isCockpitComponent,
  COCKPIT_ROOT_SLUG,
} from './cockpit-library.js';

/* ─── Spec version ──────────────────────────────────────────────────────── */

/**
 * CockpitSpec schema version. Bumped on a breaking node-shape change. Stored on
 * the version row's spec_jsonb so the renderer can refuse a spec it can't
 * interpret.
 */
export const COCKPIT_SPEC_VERSION = '1.0.0' as const;

/** Hard ceiling on total nodes in a spec tree (anti-DoS for the gates + renderer). */
export const COCKPIT_SPEC_MAX_NODES = 400;
/** Hard ceiling on tree depth. */
export const COCKPIT_SPEC_MAX_DEPTH = 12;
/**
 * Hard ceiling on the number of ADDITIONAL pages in a multi-page cockpit
 * (`spec.pages`). The implicit "main" page (`spec.root`) is always present and
 * does NOT count against this — a cockpit can therefore have up to
 * `COCKPIT_SPEC_MAX_PAGES + 1` pages total. The `COCKPIT_SPEC_MAX_NODES` budget
 * is shared ACROSS all pages (a 20-page cockpit cannot smuggle 20×400 nodes).
 */
export const COCKPIT_SPEC_MAX_PAGES = 20;

/* ─── A spec node ───────────────────────────────────────────────────────── */

/**
 * A single node. `component` MUST be a catalog slug (validated by superRefine
 * against `isCockpitComponent`). `props` is validated against the component's
 * own `propsSchema` (the deep prop check). `children` is recursive.
 *
 * Because the node is recursive AND the props validation depends on the
 * component slug (a per-node dynamic schema), we cannot express the whole thing
 * as a single static z.object — we declare a `z.lazy` recursive base and run the
 * dynamic per-component props validation in a top-level superRefine on the spec
 * (`validateNodeTree`), which has the full path context to report precise
 * issues. The base node schema below enforces the STRUCTURAL shape; the spec-
 * level refine enforces the SEMANTIC (catalog + props) shape.
 */
export interface CockpitNode {
  /** Stable node id (unique within the spec). Used by gates + renderer keys. */
  id: string;
  /** A catalog component slug. */
  component: string;
  /** Props bag — validated against the component's propsSchema. */
  props?: Record<string, unknown>;
  /** Child nodes. */
  children?: CockpitNode[];
}

const NODE_ID_RE = /^[a-z][a-z0-9_-]{0,63}$/;

const BaseNodeSchema: z.ZodType<CockpitNode> = z.lazy(() =>
  z
    .object({
      id: z
        .string()
        .regex(
          NODE_ID_RE,
          'node id must be lowercase, start with a letter, ≤64 chars (a-z 0-9 _ -)',
        ),
      component: z.string().min(1).max(80),
      props: z.record(z.string(), z.unknown()).optional(),
      children: z.array(BaseNodeSchema).max(COCKPIT_SPEC_MAX_NODES).optional(),
    })
    .strict(),
);

/* ─── A page (multi-page cockpits) ──────────────────────────────────────── */

/**
 * One page of a multi-page cockpit. `root` is its own component tree (must be a
 * `CockpitContainer`, like the spec `root`). `id` is a stable, page-unique slug
 * the renderer uses for the page-nav + the active-page key; `title` labels the
 * nav tab; `icon` is an optional catalog icon slug shown beside the title.
 *
 * Multi-page is purely additive: a spec with no `pages` is a single-page
 * cockpit whose only page is `root`. See `getCockpitPages`.
 */
export interface CockpitPage {
  id: string;
  title: string;
  icon?: string;
  root: CockpitNode;
}

/** Page-id namespace mirrors the node-id charset (separate namespace though). */
const PAGE_ID_RE = /^[a-z][a-z0-9_-]{0,63}$/;

const PageSchema = z
  .object({
    id: z
      .string()
      .regex(
        PAGE_ID_RE,
        'page id must be lowercase, start with a letter, ≤64 chars (a-z 0-9 _ -)',
      ),
    title: z.string().min(1).max(80),
    icon: z.string().max(60).optional(),
    root: BaseNodeSchema,
  })
  .strict();

/* ─── The spec ──────────────────────────────────────────────────────────── */

/**
 * The full CockpitSpec. `root` is a single node tree whose top component MUST be
 * the `CockpitContainer`. `meta` carries authoring provenance. Binding + action
 * refs inside nodes are validated against `declaredBindingIds` /
 * `declaredActionIds` so a node can never reference a binding the manifest does
 * not declare.
 */
export const CockpitSpecSchema = z
  .object({
    specVersion: z.literal(COCKPIT_SPEC_VERSION),
    /** The declared binding ids (mirrors the data_bindings manifest ids). */
    declaredBindingIds: z.array(z.string().min(1).max(120)).max(200).default([]),
    /** The declared action ids (the action bindings' actionId values). */
    declaredActionIds: z.array(z.string().min(1).max(120)).max(200).default([]),
    root: BaseNodeSchema,
    /**
     * Multi-page cockpit: ADDITIONAL pages beyond the implicit "main" page
     * (`root`). Absent/empty → a single-page cockpit. Each page's `root` is an
     * independent CockpitContainer tree; ids must be page-unique and node ids
     * stay globally unique across all pages (shared with `root`).
     */
    pages: z.array(PageSchema).max(COCKPIT_SPEC_MAX_PAGES).optional(),
    meta: z
      .object({
        title: z.string().min(1).max(200),
        archetype: z.string().max(80).optional(),
        authoredBy: z.enum(['pipeline', 'human', 'archetype']).default('pipeline'),
      })
      .optional(),
  })
  .strict()
  .superRefine((spec, ctx) => {
    validateNodeTree(spec, ctx);
  });

export type CockpitSpec = z.infer<typeof CockpitSpecSchema>;

/* ─── Tree validation (catalog + props + refs + limits) ─────────────────── */

/**
 * The slot-prop names that carry a binding ref (read/state/subscribe) or an
 * action ref, derived per component from its contract. Also returns the NESTED
 * ref paths (`array[].field`) for components that bury refs inside an array prop
 * (e.g. ButtonGroup.buttons[].action) — those are invisible to the top-level
 * slot check and validated separately by `validateNestedRefs`.
 */
function collectSlotProps(component: string): {
  bindingProps: Set<string>;
  actionProps: Set<string>;
  nestedBindingPaths: string[];
  nestedActionPaths: string[];
} {
  const contract = getCockpitComponent(component);
  const bindingProps = new Set<string>();
  const actionProps = new Set<string>();
  if (contract) {
    for (const s of contract.bindingSlots) bindingProps.add(s.prop);
    for (const s of contract.actionSlots) actionProps.add(s.prop);
  }
  return {
    bindingProps,
    actionProps,
    nestedBindingPaths: contract?.nestedBindingPaths ?? [],
    nestedActionPaths: contract?.nestedActionPaths ?? [],
  };
}

/**
 * Walk a declared nested ref path (`'<arrayProp>[].<refField>'`) on a node's
 * props and report any ref value not present in `declared`. Pushes precise z
 * issues with a path into the node (`props.<arrayProp>.<i>.<refField>`) so the
 * re-author loop can target the exact entry — mirroring the top-level ref check.
 *
 * `kind` is purely cosmetic for the message + the structured code
 * (`ACTION_REF_NOT_DECLARED` / `BINDING_REF_NOT_DECLARED`). Only single-level
 * array nesting is supported (the only shape any catalog component uses); a
 * malformed path or non-array prop is skipped (the props-schema check already
 * rejects a wrong-typed prop, so this never masks a real error).
 */
function validateNestedRefs(
  component: string,
  props: Record<string, unknown>,
  nestedPath: string,
  declared: Set<string>,
  kind: 'action' | 'binding',
  basePath: (string | number)[],
  ctx: z.RefinementCtx,
): void {
  const m = /^([a-zA-Z0-9_]+)\[\]\.([a-zA-Z0-9_]+)$/.exec(nestedPath);
  if (!m) return;
  const [, arrayProp, refField] = m;
  const arr = props[arrayProp!];
  if (!Array.isArray(arr)) return;
  const code = kind === 'action' ? 'ACTION_REF_NOT_DECLARED' : 'BINDING_REF_NOT_DECLARED';
  const noun = kind === 'action' ? 'action' : 'binding';
  for (let i = 0; i < arr.length; i++) {
    const entry = arr[i];
    if (!entry || typeof entry !== 'object') continue;
    const v = (entry as Record<string, unknown>)[refField!];
    if (typeof v === 'string' && v.length > 0 && !declared.has(v)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: { code },
        message: `[${component}] ${arrayProp}[${i}].${refField} references ${noun} '${v}' which is not declared`,
        path: [...basePath, 'props', arrayProp!, i, refField!],
      });
    }
  }
}

/**
 * Walk the node tree depth-first, enforcing:
 *   1. root component === CockpitContainer
 *   2. every node's component is a catalog slug  → COMPONENT_NOT_IN_CATALOG
 *   3. every node's props parse against the contract's propsSchema → PROP_SCHEMA_VIOLATION
 *   4. node ids are unique
 *   5. binding/action ref props reference a declared id
 *   6. node count ≤ MAX_NODES, depth ≤ MAX_DEPTH
 *
 * Reports precise z issues with a `path` into the tree so the re-author loop can
 * target the exact node.
 */
function validateNodeTree(
  spec: {
    root: CockpitNode;
    pages?: CockpitPage[];
    declaredBindingIds: string[];
    declaredActionIds: string[];
  },
  ctx: z.RefinementCtx,
): void {
  const seenIds = new Set<string>();
  const declaredBindings = new Set(spec.declaredBindingIds);
  const declaredActions = new Set(spec.declaredActionIds);
  let nodeCount = 0;

  // Root must be the container.
  if (spec.root.component !== COCKPIT_ROOT_SLUG) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Root component must be '${COCKPIT_ROOT_SLUG}', got '${spec.root.component}'`,
      path: ['root', 'component'],
    });
  }

  const walk = (node: CockpitNode, path: (string | number)[], depth: number): void => {
    nodeCount += 1;
    if (nodeCount > COCKPIT_SPEC_MAX_NODES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Spec exceeds the ${COCKPIT_SPEC_MAX_NODES}-node limit`,
        path,
      });
      return;
    }
    if (depth > COCKPIT_SPEC_MAX_DEPTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Spec exceeds the ${COCKPIT_SPEC_MAX_DEPTH}-level depth limit`,
        path,
      });
      return;
    }

    // Unique ids.
    if (seenIds.has(node.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate node id '${node.id}'`,
        path: [...path, 'id'],
      });
    }
    seenIds.add(node.id);

    // (2) catalog membership.
    if (!isCockpitComponent(node.component)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: { code: 'COMPONENT_NOT_IN_CATALOG', component: node.component },
        message: `Component '${node.component}' is not in the cockpit-library catalog`,
        path: [...path, 'component'],
      });
      // Cannot validate props for an unknown component — stop here for this node.
      return;
    }

    // (3) props schema.
    const contract = getCockpitComponent(node.component)!;
    const propsResult = contract.propsSchema.safeParse(node.props ?? {});
    if (!propsResult.success) {
      for (const issue of propsResult.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { code: 'PROP_SCHEMA_VIOLATION', component: node.component },
          message: `[${node.component}] ${issue.path.join('.') || '(root)'}: ${issue.message}`,
          path: [...path, 'props', ...issue.path],
        });
      }
    }

    // (5) ref props must reference a declared id.
    const { bindingProps, actionProps, nestedBindingPaths, nestedActionPaths } =
      collectSlotProps(node.component);
    const props = node.props ?? {};
    for (const p of bindingProps) {
      const v = props[p];
      if (typeof v === 'string' && v.length > 0 && !declaredBindings.has(v)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { code: 'BINDING_REF_NOT_DECLARED' },
          message: `[${node.component}] prop '${p}' references binding '${v}' which is not declared`,
          path: [...path, 'props', p],
        });
      }
    }
    for (const p of actionProps) {
      const v = props[p];
      if (typeof v === 'string' && v.length > 0 && !declaredActions.has(v)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          params: { code: 'ACTION_REF_NOT_DECLARED' },
          message: `[${node.component}] prop '${p}' references action '${v}' which is not declared`,
          path: [...path, 'props', p],
        });
      }
    }

    // (5b) NESTED ref props (refs buried in an array prop, e.g.
    // ButtonGroup.buttons[].action / InlineActions.actions[].action). These are
    // invisible to the top-level slot check above, so a node could otherwise
    // publish with an undeclared nested action that 404s on click.
    for (const np of nestedActionPaths) {
      validateNestedRefs(node.component, props, np, declaredActions, 'action', path, ctx);
    }
    for (const np of nestedBindingPaths) {
      validateNestedRefs(node.component, props, np, declaredBindings, 'binding', path, ctx);
    }

    // Recurse.
    const kids = node.children ?? [];
    for (let i = 0; i < kids.length; i++) {
      walk(kids[i], [...path, 'children', i], depth + 1);
    }
  };

  walk(spec.root, ['root'], 1);

  // Additional pages (multi-page cockpits). Each page root is an independent
  // CockpitContainer tree validated with the SAME shared budget (seenIds +
  // nodeCount) so node ids stay globally unique and the node ceiling is not
  // multiplied per page. Page ids have their own uniqueness namespace.
  const pages = spec.pages ?? [];
  const seenPageIds = new Set<string>();
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    if (seenPageIds.has(page.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Duplicate page id '${page.id}'`,
        path: ['pages', i, 'id'],
      });
    }
    seenPageIds.add(page.id);
    if (page.root.component !== COCKPIT_ROOT_SLUG) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Page '${page.id}' root component must be '${COCKPIT_ROOT_SLUG}', got '${page.root.component}'`,
        path: ['pages', i, 'root', 'component'],
      });
    }
    walk(page.root, ['pages', i, 'root'], 1);
  }
}

/* ─── Convenience: parse + flatten ──────────────────────────────────────── */

export interface ParsedCockpitSpec {
  ok: boolean;
  spec?: CockpitSpec;
  /** Flat structured errors (code + message + jsonPath) for the re-author loop. */
  errors: Array<{ code: string; message: string; path: string }>;
}

/**
 * Parse a candidate into a CockpitSpec, returning STRUCTURED errors suitable for
 * feeding back into the authoring re-author loop (each carries a code + a json
 * path). The codes mirror the architect rule ids
 * (COMPONENT_NOT_IN_CATALOG / PROP_SCHEMA_VIOLATION / …).
 */
export function parseCockpitSpec(candidate: unknown): ParsedCockpitSpec {
  const result = CockpitSpecSchema.safeParse(candidate);
  if (result.success) {
    return { ok: true, spec: result.data, errors: [] };
  }
  const errors = result.error.issues.map((issue) => {
    const params = (issue as { params?: { code?: string } }).params;
    const code =
      params && typeof params.code === 'string' ? params.code : 'SPEC_SHAPE_INVALID';
    return {
      code,
      message: issue.message,
      path: issue.path.map(String).join('.') || '(root)',
    };
  });
  return { ok: false, errors };
}

/**
 * Walk a parsed spec and return every node across ALL pages (flattened,
 * depth-first). Used by the gates (a11y, subscribe-wired) and the renderer. A
 * single-page spec returns just the `root` tree; a multi-page spec returns
 * `root` followed by each page's tree in page order.
 */
export function flattenSpec(spec: CockpitSpec): CockpitNode[] {
  const out: CockpitNode[] = [];
  const walk = (n: CockpitNode): void => {
    out.push(n);
    for (const c of n.children ?? []) walk(c);
  };
  walk(spec.root);
  for (const page of spec.pages ?? []) walk(page.root);
  return out;
}

/* ─── Multi-page normalization ──────────────────────────────────────────── */

/** A resolved, render-ready page (the implicit "main" page + any extras). */
export interface ResolvedCockpitPage {
  id: string;
  title: string;
  icon?: string;
  root: CockpitNode;
}

/** The stable id of the implicit first page backed by `spec.root`. */
export const COCKPIT_MAIN_PAGE_ID = 'page-main' as const;

/**
 * Normalize a spec to its full ordered page list for the renderer: the implicit
 * "main" page (backed by `spec.root`, titled from `meta.title`) followed by any
 * declared `pages`. A single-page spec yields a 1-element array — the renderer
 * shows page-nav chrome only when this list has length > 1, so existing
 * single-page cockpits render exactly as before.
 */
export function getCockpitPages(spec: {
  root: CockpitNode;
  pages?: CockpitPage[];
  meta?: { title?: string };
}): ResolvedCockpitPage[] {
  const main: ResolvedCockpitPage = {
    id: COCKPIT_MAIN_PAGE_ID,
    title: spec.meta?.title?.trim() || 'Main',
    root: spec.root,
  };
  const extra = (spec.pages ?? []).map((p) => ({
    id: p.id,
    title: p.title,
    ...(p.icon ? { icon: p.icon } : {}),
    root: p.root,
  }));
  return [main, ...extra];
}
